/**
 * Component Resolver - Sequence-Driven Lazy Loading
 *
 * Resolves React components by sequence number instead of hard-coded file paths.
 * Eliminates fragile import statements that break during file renames.
 *
 * Architecture:
 * Database (seq) → Manifest (filename) → Glob registry → Lazy component
 *
 * @see docs/agent-contracts/AGENT_COMPONENT_RESOLUTION_STANDARD.md
 */

import { lazy } from 'react';
import tsxInventory from '../../public/tsx-inventory-manifest.json';

/**
 * Glob registries for each component folder
 * These are populated at build time by Vite's import.meta.glob
 *
 * ARCHITECTURE: Prefix = Logical Type, Folder = Physical Size
 * - wdgt_ = widget prefix → RegisteredTabs (small components)
 * - scr_ = screen prefix → RegisteredTabs (functional screens)
 * - mod_ = module prefix → RegisteredModules (large hosts)
 *
 * LAZY LOADING STRATEGY (2026-06-21):
 * - Glob registries use LAZY imports (no { eager: true })
 * - Vite plugin (vite-plugin-resolve-sequences.ts) transforms EAGER components
 * - EAGER components (Prop_Dependant_Parent_Seq = 1) become static imports
 * - LAZY components (Prop_Dependant_Parent_Seq = NULL) use glob registry
 * - Database-driven: Change flag in CR2 → regenerate manifest → rebuild
 */
// EXCLUDE (2026-10-28): mobuni_* files are React Native source carried in
// this web repo purely as a tracked reference/snapshot for the Mobile App
// Developer Portal (Component_Source_Snapshot sync) - they import from
// 'react-native' (not installed at the web project root) and are never
// routed/rendered on web, so they must never be a build candidate here.
const registeredTabs = import.meta.glob([
    '../TGC/RegisteredTabs/*.tsx',
    '!../TGC/RegisteredTabs/mobuni_*.tsx',
]);
const registeredModules = import.meta.glob('../TGC/RegisteredModules/*.tsx');
const registeredBinders = import.meta.glob('../TGC/RegisteredBinders/*.tsx');
// EAGER (2026-09-08): directory host templates (nav_*, gate_*) are a small
// shared set that render database-driven buttons/cards/menus (data is already
// a synchronous static import - see useDirectoryManifest.ts). Eager-loading
// the host itself closes the last async gap: no Suspense-pending window where
// the host could mount before its dirSeq/manifest lookup is ready.
const registeredDirectories = import.meta.glob('../TGC/RegisteredDirectories/*.tsx', {
    eager: true,
});
const registeredGateways = import.meta.glob('../TGC/RegisteredGateways/*.tsx');
const registeredHierComposer = import.meta.glob('../TGC/RegisteredHierComposer/*.tsx');
const registeredHosts = import.meta.glob('../TGC/RegisteredHosts/*.tsx');
const registeredMarketingSites = import.meta.glob('../TGC/RegisteredMarketingSites/*.tsx');
const registeredTheaterScreens = import.meta.glob('../TGC/RegisteredTheaterScreens/*.tsx');
// CONSOLIDATED (2026-09-29): these 4 registries used to exist ONLY in
// componentLoader.ts's own separate copy of this same glob-registry table
// (createComponentBySeq). That meant a sequence resolved from a top-level
// route (componentLoader.ts) could succeed while the SAME sequence resolved
// as an in-module tab (this file) threw "Unknown folder" - this file never
// globbed these 4 folders at all. componentLoader.ts now delegates to this
// file instead of keeping its own copy, so this is the one and only place
// these folders are globbed.
const registeredComponentsTGC = import.meta.glob('../TGC/RegisteredComponents/*.tsx');
const registeredComponentsLCV = import.meta.glob('../LCV/RegisteredComponents/*.tsx');
const registeredComponentsDNALCV = import.meta.glob('../LCV/RegisteredComponents-DNA/*.tsx');
const registeredComponentsMainStreet = import.meta.glob('../MainStreet/RegisteredComponents/*.tsx');

/**
 * Get glob registry by ROOT-QUALIFIED folder key (extracted from breadcrumb_path)
 *
 * ARCHITECTURE: Manifest contains actual physical location, no prefix guessing needed
 * breadcrumb_path: "TGC/RegisteredDirectories/nav_tgc_10000.tsx" → folderKey: "TGC/RegisteredDirectories"
 * breadcrumb_path: "LCV/RegisteredComponents/dest_lcv_10558_....tsx" → folderKey: "LCV/RegisteredComponents"
 *
 * FIXED (2026-09-29): previously keyed by the bare leaf folder name only
 * (e.g. "RegisteredComponents") and hard-required the root to be "TGC" -
 * silently threw "Invalid breadcrumb_path format" for every real LCV/*
 * component (46+ entries in tsx-inventory-manifest.json). Root-qualifying
 * the key (matching componentLoader.ts's proven approach) removes both the
 * TGC-only restriction and the leaf-name collision risk between e.g.
 * TGC/RegisteredComponents and LCV/RegisteredComponents.
 */
function getGlobRegistry(folderKey: string) {
    const registryMap: Record<string, any> = {
        'TGC/RegisteredTabs': registeredTabs,
        'TGC/RegisteredModules': registeredModules,
        'TGC/RegisteredBinders': registeredBinders,
        'TGC/RegisteredDirectories': registeredDirectories,
        'TGC/RegisteredGateways': registeredGateways,
        'TGC/RegisteredHierComposer': registeredHierComposer,
        'TGC/RegisteredHosts': registeredHosts,
        'TGC/RegisteredMarketingSites': registeredMarketingSites,
        'TGC/RegisteredTheaterScreens': registeredTheaterScreens,
        'TGC/RegisteredComponents': registeredComponentsTGC,
        'LCV/RegisteredComponents': registeredComponentsLCV,
        'LCV/RegisteredComponents-DNA': registeredComponentsDNALCV,
        'MainStreet/RegisteredComponents': registeredComponentsMainStreet,
    };

    const registry = registryMap[folderKey];
    if (!registry) {
        throw new Error(
            `Unknown folder: ${folderKey}. Expected one of: ${Object.keys(registryMap).join(', ')}`,
        );
    }

    return registry;
}

/**
 * Find manifest entry by sequence number
 *
 * Architecture: Manifest should contain seq from Component_Registry2.Sequence_Num
 * where Button_ID = Sequence_Num and TSXFileName_Target is computed via
 * fn_Generate_TSXFileName_Target().
 *
 * @param seq - Component sequence number from Component_Registry2.Sequence_Num
 * @returns Manifest entry with file_name and breadcrumb_path
 */
function findManifestEntry(seq: number) {
    // First try: Look for the authoritative sequence_num field, populated by
    // generate-tsx-inventory.js from each file's @tg-seq comment. This field is
    // ALREADY deduplicated at generation time (files sharing a sequence_num are
    // excluded entirely - see "SAFETY NET" block in that script) so a match here
    // is guaranteed unambiguous.
    //
    // BUG HISTORY (fixed 2026-09-08): this used to check `e.seq === seq`, but no
    // manifest entry has ever had a `seq` field (the generator only emits
    // `sequence_num`) - so this branch silently never matched, for ANY sequence,
    // and every single lookup fell through to a filename-regex fallback. That
    // fallback ignored the dedup safety net entirely and matched on the
    // FILENAME's embedded number, which can differ from a file's actual
    // @tg-seq comment (stale copy/paste twins, renamed-but-not-fully-updated
    // files). Net effect: navigation intermittently resolved to a wrong/stale
    // component instead of the real one or a clean error - looked like a
    // "race condition" from the outside. See
    // scripts/auditors/reports/DUPLICATE_SEQUENCE_AUDIT_2026-09-08.md.
    //
    // REMOVED (2026-09-08): the filename-regex fallback itself. It is a second
    // instance of the same failure mode - silently resolving to a plausible-
    // looking-but-wrong answer instead of failing loudly. Manifest generation
    // (generate-tsx-inventory.js) already excludes any file missing a
    // sequence_num or colliding with another file's sequence_num, so there is
    // no legitimate case where a real match only exists via filename-guessing.
    // If this returns undefined, the caller throws - by design.
    return (tsxInventory.data as any[]).find((e: any) => e.sequence_num === seq);
}

/**
 * Resolve a component by sequence number
 *
 * @param seq - Component sequence number from Component_Registry2
 * @returns Lazy-loaded React component
 * @throws Error if sequence not found in manifest or glob registry
 *
 * @example
 * const MyTab = resolveComponentBySeq(11009);
 * <Suspense fallback={<Loading />}>
 *   <MyTab />
 * </Suspense>
 */
export function resolveComponentBySeq(
    seq: number,
    // Optional Display_Order from Module_Tab_Registry. Not used in resolution
    // logic - purely self-documenting so a fixmod_ (FLEX_OUTLET) BLOCK 3
    // declaration line shows its DB-driven ordinal at the call site, the same
    // way a mod_ host's generated TAB_REGISTRY array does. Kept as a same-
    // signature overload (not a separate function name) so every existing
    // resolveComponentBySeq(seq) call site stays valid.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ordinal?: number,
): React.LazyExoticComponent<React.ComponentType<any>> {
    // Step 1: Find in manifest
    const entry = findManifestEntry(seq);
    if (!entry) {
        // Resolve and fail here - no self-heal reload, no redirect. Caller
        // (ComponentViewer, ModuleHost, tab host, etc.) is responsible for
        // catching this and showing an inline error in place.
        throw new Error(`Component sequence ${seq} not registered in manifest`);
    }

    // Step 2: Extract ROOT-QUALIFIED folder key from breadcrumb_path (manifest
    // knows actual physical location - TGC/*, LCV/*, and MainStreet/* roots)
    // Example: "TGC/RegisteredDirectories/nav_tgc_10000.tsx" → "TGC/RegisteredDirectories"
    // Example: "LCV/RegisteredComponents/dest_lcv_10558_....tsx" → "LCV/RegisteredComponents"
    const pathParts = entry.breadcrumb_path.split('/');
    if (pathParts.length < 3) {
        throw new Error(`Invalid breadcrumb_path format: ${entry.breadcrumb_path}`);
    }
    const folderKey = `${pathParts[0]}/${pathParts[1]}`; // e.g. "TGC/RegisteredModules"

    // Step 3: Get glob registry for actual folder
    const registry = getGlobRegistry(folderKey);

    // Step 4: Build import path
    const importPath = `../${folderKey}/${entry.file_name}`;

    // Step 5: Resolve from glob registry
    const moduleLoader = registry[importPath];
    if (!moduleLoader) {
        throw new Error(`Component ${entry.file_name} not found in glob registry at ${importPath}`);
    }

    // Step 6: Return lazy component
    // LAZY GLOB: registry[path] is a () => Promise<Module> function (most folders).
    // EAGER GLOB: registry[path] is already the resolved module object (directory
    // hosts only - see registeredDirectories above). Normalize both into
    // React.lazy so callers always get a LazyExoticComponent either way - the
    // eager case resolves an already-fulfilled promise (no chunk fetch, no
    // network wait, no mount-before-prop-ready gap).
    // Direct mount, no retry/reload wrapper (removed 2026-10-23): onClick -> navigate
    // -> mount must be a direct path with nothing in between, per platform principle.
    if (typeof moduleLoader === 'function') {
        const loader = moduleLoader as () => Promise<{ default: React.ComponentType<any> }>;
        return lazy(loader);
    }
    return lazy(() => Promise.resolve(moduleLoader as { default: React.ComponentType<any> }));
}

/**
 * Bulk resolve multiple components by sequence numbers
 * Useful for multi-tab hosts with many children
 *
 * @param seqs - Array of sequence numbers
 * @returns Map of seq → lazy component
 *
 * @example
 * const components = bulkResolveBySeq([11009, 10370, 10814]);
 * components.get(11009) // Lazy component
 */
export function bulkResolveBySeq(
    seqs: number[],
): Map<number, React.LazyExoticComponent<React.ComponentType<any>>> {
    const resolved = new Map();

    for (const seq of seqs) {
        try {
            resolved.set(seq, resolveComponentBySeq(seq));
        } catch (error) {
            // Continue processing other sequences - pre-commit validates sequences
        }
    }

    return resolved;
}

/**
 * Inject standard module template (ModuleHost V5 pattern)
 *
 * BUILD-TIME TRANSFORMATION: Vite plugin replaces entire function with complete ModuleHost implementation
 *
 * The Vite plugin replaces this statement:
 *   const Self = injectStdModuleTemplate(10019);
 *
 * With generated code:
 * - Tab imports (import Tab10681 from '../RegisteredTabs/...')
 * - TAB_REGISTRY const
 * - ModuleHost import
 * - Complete component implementation
 *
 * Single source of truth: Module_Tab_Registry database table
 * To modify tabs: Update Module_Tab_Registry → npm run sync-manifests → rebuild
 *
 * @param moduleSeq - Module sequence number (e.g., 10019)
 * @returns Component function (replaced at build time)
 *
 * @example
 * // Source code (2-line pattern):
 * const Self = injectStdModuleTemplate(10019);
 * export default Self;
 *
 * // After Vite transformation (build):
 * import ModuleHost from '../infrastructure/ModuleHost';
 * import Tab10681 from '../RegisteredTabs/scr_tgc_10681_...';
 * const TAB_REGISTRY = [{ seq: 10681, label: 'Payroll Profile', component: Tab10681 }];
 * const Self = () => <ModuleHost moduleSeq={10019} tabs={TAB_REGISTRY} />;
 */
export function injectStdModuleTemplate(moduleSeq: number): React.ComponentType<any> {
    // This function body is NEVER executed - Vite replaces the entire statement
    // Returning placeholder component satisfies TypeScript during development
    console.warn(
        `⚠️  injectStdModuleTemplate(${moduleSeq}) was not transformed by Vite plugin. ` +
            `Build may have failed. Check tab-assignments-manifest.json and Vite config.`,
    );
    return () => null;
}

/**
 * Resolve all tabs for a module by module sequence number
 *
 * BUILD-TIME TRANSFORMATION: Vite plugin replaces entire statement with BLOCK 3 + BLOCK 4
 *
 * The Vite plugin (vite-plugin-resolve-sequences.ts) replaces this statement:
 *   const TAB_REGISTRY = resolveModuleTabs(10307);
 *
 * With generated code:
 * - BLOCK 3: Static imports (import Tab10495 from '../RegisteredTabs/...')
 * - BLOCK 4: const TAB_REGISTRY = [{ seq, label, component }]
 *
 * Single source of truth: Module_Tab_Registry database table
 * To modify tabs: Update Module_Tab_Registry → npm run sync-manifests → rebuild
 *
 * @param moduleSeq - Module sequence number (e.g., 10307)
 * @returns TAB_REGISTRY type for TypeScript (replaced at build time)
 *
 * @example
 * // Source code (development):
 * const TAB_REGISTRY = resolveModuleTabs(10307);
 *
 * // After Vite transformation (build):
 * import Tab10495 from '../RegisteredTabs/scr_tgc_10495_...';
 * import Tab10466 from '../RegisteredTabs/scr_tgc_10466_...';
 * const TAB_REGISTRY = [
 *     { seq: 10495, label: 'Solution Module Registry', component: Tab10495 },
 *     { seq: 10466, label: 'Solution Strategy', component: Tab10466 },
 * ];
 */
export function resolveModuleTabs(moduleSeq: number): Array<{
    seq: number;
    label: string;
    component: React.ComponentType<any>;
}> {
    // This function body is NEVER executed - Vite replaces the entire statement
    // Returning empty array satisfies TypeScript during development
    // At runtime (if build failed), this will cause empty tabs instead of crash
    console.warn(
        `⚠️  resolveModuleTabs(${moduleSeq}) was not transformed by Vite plugin. ` +
            `Build may have failed. Check tab-assignments-manifest.json and Vite config.`,
    );
    return [];
}

/**
 * Helper: Map tab definitions with sequence-driven resolution
 *
 * @example
 * const tabs = resolveTabDefinitions([
 *   { seq: 11009, label: 'AG CRUD' },
 *   { seq: 10370, label: 'Metrics' }
 * ]);
 */
export function resolveTabDefinitions<T extends { seq: number }>(
    definitions: T[],
): Array<T & { component: React.LazyExoticComponent<React.ComponentType<any>> }> {
    return definitions.map((def) => ({
        ...def,
        component: resolveComponentBySeq(def.seq),
    }));
}
